/**
 * @fileoverview Links section for the job contact detail panel.
 *
 * One section for both ways a job contact is linked: to other contacts on
 * the job (contact people, or the company a person speaks for) and to the
 * firm contacts directory. Contact-people linking is first because that is who
 * documents address; the firm link is the fallback for empty email, phone,
 * and notes.
 */

import { Building2 } from "lucide-react";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { ContactPeopleSection } from "./ContactPeopleSection";
import { FirmContactLinkPicker } from "./FirmContactLinkPicker";
import { FIRM_CATEGORY_META, type JobContactRow } from "./types";

interface ContactLinksSectionProps {
  contact: JobContactRow;
  /** Other rows on this job, for the contact-person picker. */
  jobContacts: Array<JobContactRow>;
  disabled?: boolean;
  onLinkFirm: (firmContactId: Id<"firmContacts">) => void;
  onUnlinkFirm: () => void;
}

export function ContactLinksSection({
  contact,
  jobContacts,
  disabled,
  onLinkFirm,
  onUnlinkFirm,
}: ContactLinksSectionProps) {
  return (
    <section className="flex flex-col gap-4">
      <h3 className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
        Links
      </h3>

      <ContactPeopleSection
        contact={contact}
        jobContacts={jobContacts}
        disabled={disabled}
      />

      <div className="flex flex-col gap-2">
        <h4 className="font-sans text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Firm contacts
        </h4>
        {contact.firmContact ? (
          <div className="flex items-start justify-between gap-3 rounded-lg border border-border bg-muted/20 p-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="truncate text-sm font-medium text-foreground">
                  {contact.firmContact.displayName}
                </span>
                <Badge
                  color={
                    FIRM_CATEGORY_META[contact.firmContact.category].badgeColor
                  }
                  variant="soft"
                  size="sm"
                  className="px-1 py-0 text-[10px]"
                >
                  {FIRM_CATEGORY_META[contact.firmContact.category].shortLabel}
                </Badge>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                {contact.firmContact.email && (
                  <span>{contact.firmContact.email}</span>
                )}
                {contact.firmContact.phone && (
                  <span className="font-mono tabular-nums">
                    {contact.firmContact.phone}
                  </span>
                )}
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1.5">
              <FirmContactLinkPicker
                orgId={contact.orgId}
                currentFirmContactId={contact.firmContactId}
                onPick={onLinkFirm}
                disabled={disabled}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={onUnlinkFirm}
                disabled={disabled}
                className="h-7 text-xs"
              >
                Unlink
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-border bg-muted/10 p-3">
            <p className="text-xs text-muted-foreground">
              None linked yet. Linking will apply firm contact email, phone and
              notes when these fields are missing in this contact.
            </p>
            <FirmContactLinkPicker
              orgId={contact.orgId}
              onPick={onLinkFirm}
              disabled={disabled}
            />
          </div>
        )}
      </div>
    </section>
  );
}
